import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomDropdown from "../Components/CustomDropdown";
import VendorProductSelector from "../Components/VendorProductSelector";
import LoadingSpinner from "../Components/LoadingSpinner";
import { countryCodes } from "../data/countryCodes";
import {
  CURRENCY_DROPDOWN_OPTIONS,
  CURRENCY_OPTIONS,
  getCurrencyCode,
  QUANTITY_UNIT_OPTIONS,
} from "../data/currencyOptions";
import {
  createVendorProductsAction,
  getVendorProductAction,
  getVendorProductsAction,
  isVendorProductResponseSuccessful,
  updateVendorProductAction,
} from "../actions/vendorProduct";
import { uploadVendorDocumentAction } from "../actions/auth";
import { toogleErrorMessage } from "../actions/alertMessage";
import { backendUrl } from "../Router/constant";
import {
  handleNumericInputKeyDown,
  sanitizeNumericInput,
} from "../utils/numericInput";

const EMPTY_FORM = {
  productName: "",
  productCode: "",
  sectorName: "",
  price: "",
  quantity: "",
  currency: "USD",
  quantityUnit: "",
  description: "",
  servicesSupplied: "",
  brandsRepresented: "",
  manufacturerDistributor: "",
  countriesManufactured: [],
  countriesSupplied: [],
  productCatalogue: "",
  productCatalogueFileName: "",
  productSpecifications: "",
  productSpecificationsFileName: "",
  hsCodes: "",
  normalLeadTime: "",
  normalLeadTimeUnit: "days",
  productionCapacity: "",
  productionCapacityUnit: "MT",
  monthlySupplyCapacity: "",
  annualSupplyCapacity: "",
  supplyCapacity: "",
  supplyCapacityPeriod: "monthly",
  supplyCapacityUnit: "MT",
  status: "draft",
};

const normalizeQuantityUnit = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    value = value.value ?? value.code ?? value.unit ?? value.name ?? value.label ?? "";
  }
  return String(value).trim();
};

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 6L18 18M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const COUNTRY_OPTIONS = countryCodes
  .map((country) => ({
    value: country.code,
    label: country.name,
    searchText: `${country.name} ${country.code}`,
  }))
  .sort((left, right) => left.label.localeCompare(right.label));

const LEAD_TIME_UNIT_OPTIONS = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];
const SUPPLY_PERIOD_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "annual", label: "Annual" },
];

const normalizeTextValue = (value) =>
  Array.isArray(value) ? value.join(", ") : value == null ? "" : String(value);

const splitTextValues = (value) => {
  const values = Array.isArray(value) ? value : String(value || "").split(",");
  return [
    ...new Set(
      values
        .map((entry) => {
          if (entry && typeof entry === "object") {
            return String(
              entry.name || entry.label || entry.value || entry.code || "",
            ).trim();
          }
          return String(entry || "").trim();
        })
        .filter(Boolean),
    ),
  ];
};

const getUploadReference = (value) => {
  if (Array.isArray(value)) return getUploadReference(value[0]);
  if (value && typeof value === "object") {
    return (
      value.uploadId ||
      value.fileId ||
      value._id ||
      value.id ||
      value.url ||
      value.path ||
      ""
    );
  }
  if (typeof value !== "string") return "";
  const uploadPath = value.match(/\/uploads\/([^/?#]+)/i)?.[1];
  return uploadPath ? decodeURIComponent(uploadPath) : value;
};

const getUploadReferences = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return [
    ...new Set(
      values
        .map(getUploadReference)
        .filter((reference) => reference && !/^(data:|blob:)/i.test(reference)),
    ),
  ];
};

const getUploadPreview = (value) => {
  const reference = getUploadReference(value);
  if (!reference) return "";
  return /^(https?:|data:|blob:|\/)/i.test(reference)
    ? reference
    : backendUrl + "/uploads/" + encodeURIComponent(reference);
};

const getNumericValue = (value) => {
  if (value == null || value === "") return "";
  const parsed = Number(
    String(value)
      .replace(/,/g, "")
      .match(/-?\d+(?:\.\d+)?/)?.[0],
  );
  return Number.isFinite(parsed) ? parsed : "";
};

const formatAmount = (value, unit) =>
  value === "" || value == null ? "" : `${value}${unit ? ` ${unit}` : ""}`;

const countryNamesForApi = (values) =>
  normalizeCountryValues(values).map(
    (code) =>
      countryCodes.find((country) => country.code === code)?.name || code,
  );

const manufacturerValueForApi = (value) => {
  if (value === "manufacturer") return "Manufacturer";
  if (value === "distributor") return "Distributor";
  if (value === "both") return "Manufacturer and Distributor";
  return value || "";
};

const COUNTRY_CODE_ALIASES = { uae: "AE", usa: "US", uk: "GB" };

const normalizeCountryValues = (value) => {
  const values = Array.isArray(value)
    ? value
    : typeof value === "string" && value.includes(",")
      ? value.split(",").map((country) => country.trim())
      : value
        ? [value]
        : [];
  return [
    ...new Set(
      values
        .map((country) => {
          const raw =
            typeof country === "object" && country !== null
              ? country.code ||
                country.isoCode ||
                country.name ||
                country.label ||
                country.value ||
                ""
              : country;
          const matchedCountry = countryCodes.find(
            (candidate) =>
              candidate.code.toLowerCase() === String(raw).toLowerCase() ||
              candidate.name.toLowerCase() === String(raw).toLowerCase(),
          );
          return (
            matchedCountry?.code ||
            COUNTRY_CODE_ALIASES[String(raw).toLowerCase()] ||
            String(raw)
          );
        })
        .filter(Boolean),
    ),
  ];
};

function normalizeProduct(item) {
  if (!item) return null;
  const product =
    (item.product && typeof item.product === "object" ? item.product : null) ||
    (item.productId && typeof item.productId === "object"
      ? item.productId
      : null) ||
    {};
  const sector =
    (item.sector && typeof item.sector === "object" ? item.sector : null) ||
    (item.sectorId && typeof item.sectorId === "object"
      ? item.sectorId
      : null) ||
    (product.sector && typeof product.sector === "object"
      ? product.sector
      : null) ||
    {};
  const productId =
    (typeof item.productId === "string" || typeof item.productId === "number"
      ? item.productId
      : "") ||
    product._id ||
    product.id ||
    "";
  const sectorId =
    (typeof item.sectorId === "string" || typeof item.sectorId === "number"
      ? item.sectorId
      : "") ||
    sector._id ||
    sector.id ||
    "";
  const apiId =
    item.apiId ||
    item.vendorProductId ||
    item._id ||
    item.id ||
    productId ||
    "";
  const rawUnit = item.quantityUnit || item.unit || "unit";
  const quantityUnit = normalizeQuantityUnit(rawUnit) || "unit";
  const currency = getCurrencyCode(item.currency || item.currencySymbol);
  const images = Array.isArray(item.images)
    ? item.images
    : Array.isArray(item.galleryImages)
      ? item.galleryImages
      : [];
  const mainImageValue =
    item.mainImage || product.mainImage || images[0] || null;
  const mainImageReference = getUploadReference(mainImageValue);
  // Older product records stored the main image at images[0]. Newer records
  // provide it separately, so exclude it from the gallery if an API response
  // still includes it in both places.
  const galleryImageValues =
    item.mainImage || product.mainImage
      ? images.filter((image) => {
          const reference = getUploadReference(image);
          return !mainImageReference || reference !== mainImageReference;
        })
      : images.slice(1);
  const supplyPeriod = String(
    item.supplyCapacityPeriod ||
      (/annual/i.test(String(item.monthlyAnnualSupplyCapacity || ""))
        ? "annual"
        : "monthly"),
  ).toLowerCase();
  const combinedSupplyCapacity =
    item.supplyCapacity ?? getNumericValue(item.monthlyAnnualSupplyCapacity);
  const supplierType = String(
    item.manufacturerDistributor || item.supplierType || "",
  ).toLowerCase();

  return {
    ...item,
    id: String(apiId),
    apiId: String(apiId),
    productId: String(productId),
    sectorId: String(sectorId),
    productName: normalizeTextValue(
      item.productName ||
        item.productsSupplied ||
        item.customProductName ||
        product.name ||
        product.title ||
        product.productName ||
        item.templateName ||
        "",
    ),
    productCode: item.productCode || item.templateId || "",
    sectorName:
      item.sectorName ||
      sector.name ||
      sector.title ||
      item.category ||
      item.commodity ||
      "",
    price: item.price ?? "",
    quantity:
      item.quantity ?? item.minimumOrderQuantity ?? getNumericValue(item.moq),
    quantityUnit: normalizeQuantityUnit(
      item.quantityUnit ||
        item.unit ||
        (typeof item.moq === "string"
          ? item.moq.match(/\d[\d,.]*\s*(.*)$/)?.[1]
          : "") ||
        quantityUnit,
    ),
    currency: getCurrencyCode(item.currency || item.currencySymbol || currency),
    description:
      item.productDescriptions || item.description || product.description || "",
    servicesSupplied: normalizeTextValue(
      item.servicesSupplied || item.services,
    ),
    brandsRepresented: normalizeTextValue(
      item.brandsRepresented || item.brands,
    ),
    manufacturerDistributor: /both|and|&/.test(supplierType)
      ? "both"
      : supplierType.includes("distributor")
        ? "distributor"
        : supplierType.includes("manufacturer")
          ? "manufacturer"
          : supplierType,
    countriesManufactured: normalizeCountryValues(
      item.countriesManufactured || item.manufacturingCountries,
    ),
    countriesSupplied: normalizeCountryValues(
      item.countriesSupplied || item.supplyCountries,
    ),
    productCatalogue:
      item.productCatalogue?.data ||
      getUploadReference(item.productCatalogue) ||
      "",
    productCatalogueFileName:
      item.productCatalogueFileName || item.productCatalogue?.name || "",
    productSpecifications:
      item.productSpecifications?.data ||
      getUploadReference(item.productSpecifications) ||
      normalizeTextValue(item.specifications),
    productSpecificationsFileName:
      item.productSpecificationsFileName ||
      item.productSpecifications?.name ||
      "",
    hsCodes: normalizeTextValue(item.hsCodes || item.hsCode),
    normalLeadTime: getNumericValue(item.normalLeadTime ?? item.leadTime),
    normalLeadTimeUnit: String(
      item.normalLeadTimeUnit ||
        item.leadTimeUnit ||
        (typeof item.normalLeadTime === "string"
          ? item.normalLeadTime.replace(/^\s*[\d,.]+\s*/, "")
          : "") ||
        "days",
    ).toLowerCase(),
    productionCapacity: getNumericValue(item.productionCapacity),
    productionCapacityUnit:
      item.productionCapacityUnit ||
      item.capacityUnit ||
      (typeof item.productionCapacity === "string"
        ? item.productionCapacity.match(/\d[\d,.]*\s*([a-zA-Z]+)/)?.[1]
        : "") ||
      "MT",
    monthlySupplyCapacity:
      item.monthlySupplyCapacity ??
      (supplyPeriod === "monthly" ? combinedSupplyCapacity : ""),
    annualSupplyCapacity:
      item.annualSupplyCapacity ??
      (supplyPeriod === "annual" ? combinedSupplyCapacity : ""),
    supplyCapacity:
      item.supplyCapacity ??
      item.monthlySupplyCapacity ??
      item.annualSupplyCapacity ??
      combinedSupplyCapacity,
    supplyCapacityPeriod: supplyPeriod,
    supplyCapacityUnit: item.supplyCapacityUnit || item.capacityUnit || "MT",
    status:
      item.status?.toLowerCase() === "active" || item.isActive
        ? "active"
        : "draft",
    mainImageUploadId: getUploadReferences(mainImageValue)[0] || "",
    galleryImageUploadIds: getUploadReferences(galleryImageValues),
    mainImage: getUploadPreview(mainImageValue),
    galleryImages: galleryImageValues.map(getUploadPreview).filter(Boolean),
  };
}

function getStoredProduct(id) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem("selectedProduct") || "null",
    );

    if (!stored) {
      return null;
    }

    if (id && String(stored.id) !== String(id)) {
      return null;
    }

    return normalizeProduct(stored);
  } catch {
    return null;
  }
}

export default function AddProduct({ mode = "add", product = null, onSave }) {
  const { t, text } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const vendorProductState = useSelector((state) => state.vendorProduct || {});

  const isAdd = mode === "add";
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const selectedProduct = useMemo(() => {
    if (isAdd) return null;
    const detail = normalizeProduct(vendorProductState.detail);
    const matchingDetail =
      detail && (!id || !detail.apiId || String(detail.apiId) === String(id))
        ? detail
        : null;
    return (
      matchingDetail ||
      normalizeProduct(product) ||
      normalizeProduct(location.state?.product) ||
      getStoredProduct(id)
    );
  }, [id, isAdd, location.state, product, vendorProductState.detail]);

  const [mainImage, setMainImage] = useState(
    () => selectedProduct?.mainImage || "",
  );

  const [galleryImages, setGalleryImages] = useState(
    () => selectedProduct?.galleryImages || [],
  );
  const [mainImageUploadId, setMainImageUploadId] = useState(
    () => selectedProduct?.mainImageUploadId || "",
  );
  const [galleryImageUploadIds, setGalleryImageUploadIds] = useState(
    () => selectedProduct?.galleryImageUploadIds || [],
  );
  const [fileUploadCount, setFileUploadCount] = useState(0);

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...(selectedProduct || {}),
  }));
  const [selectionGroups, setSelectionGroups] = useState([
    { key: "selection-1", sectorId: "", productIds: [], customProducts: [] },
  ]);

  useEffect(() => {
    if (!isAdd && id) dispatch(getVendorProductAction(id));
  }, [dispatch, id, isAdd]);

  useEffect(() => {
    if (isAdd) {
      setForm({
        ...EMPTY_FORM,
      });

      setMainImage("");
      setGalleryImages([]);
      setMainImageUploadId("");
      setGalleryImageUploadIds([]);
      setSelectionGroups([
        {
          key: "selection-1",
          sectorId: "",
          productIds: [],
          customProducts: [],
        },
      ]);

      return;
    }

    if (!selectedProduct) {
      return;
    }

    setForm({
      ...EMPTY_FORM,
      ...selectedProduct,

      currency: selectedProduct.currency || "USD",
    });

    setMainImage(selectedProduct.mainImage || "");
    setMainImageUploadId(selectedProduct.mainImageUploadId || "");

    setGalleryImages(selectedProduct.galleryImages || []);
    setGalleryImageUploadIds(selectedProduct.galleryImageUploadIds || []);
    setSelectionGroups([
      {
        key: "selection-1",
        sectorId: selectedProduct.sectorId || "",
        productIds: selectedProduct.productId
          ? [String(selectedProduct.productId)]
          : [],
        customProducts:
          !selectedProduct.productId && selectedProduct.productName
            ? [selectedProduct.productName]
            : [],
      },
    ]);
  }, [isAdd, selectedProduct]);

  const selectionSummary = useMemo(() => {
    const sectors = vendorProductState.sectors || [];
    const productsBySector = vendorProductState.sectorProducts || {};
    const sectorNames = [];
    const productNames = [];
    selectionGroups.forEach((group) => {
      const sector = sectors.find(
        (item) =>
          String(item?._id || item?.id || item?.sectorId || "") ===
          String(group.sectorId),
      );
      const sectorName =
        sector?.name || sector?.title || sector?.sectorName || "";
      if (sectorName && !sectorNames.includes(sectorName))
        sectorNames.push(sectorName);
      const selectedProductId = (group.productIds || [])[0];
      if (selectedProductId) {
        const item = (productsBySector[group.sectorId] || []).find(
          (product) =>
            String(product?._id || product?.id || product?.productId || "") ===
            String(selectedProductId),
        );
        const name =
          item?.name ||
          item?.title ||
          item?.productName ||
          item?.templateName ||
          (selectedProduct?.productId === String(selectedProductId)
            ? selectedProduct.productName
            : "");
        if (name && !productNames.includes(name)) productNames.push(name);
      } else {
        const customProduct = (group.customProducts || [])[0];
        if (customProduct && !productNames.includes(customProduct))
          productNames.push(customProduct);
      }
    });
    return {
      sectorName: sectorNames.join(", ") || selectedProduct?.sectorName || "",
      productName:
        productNames.join(", ") || selectedProduct?.productName || "",
    };
  }, [
    selectionGroups,
    selectedProduct,
    vendorProductState.sectors,
    vendorProductState.sectorProducts,
  ]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      sectorName: selectionSummary.sectorName,
      productName: selectionSummary.productName,
    }));
  }, [selectionSummary.sectorName, selectionSummary.productName]);

  const handleChange = (e) => {
    if (isView) {
      return;
    }

    const { name } = e.target;
    const value =
      e.target.type === "number"
        ? sanitizeNumericInput(e.target.value)
        : e.target.value;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const readImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);

      reader.readAsDataURL(file);
    });
  };

  const uploadFile = async (file) => {
    setFileUploadCount((count) => count + 1);
    try {
      return await dispatch(uploadVendorDocumentAction(file));
    } finally {
      setFileUploadCount((count) => Math.max(0, count - 1));
    }
  };

  const handleMainImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || isView) return;
    setMainImage(await readImage(file));
    setMainImageUploadId("");
    event.target.value = "";
    const result = await uploadFile(file);
    if (result?.success && result.uploadId) {
      setMainImageUploadId(result.uploadId);
    }

  };

  const handleGalleryImages = async (event) => {
    if (isView) return;
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const selectedFiles = files.slice(0, Math.max(0, 4 - galleryImages.length));
    event.target.value = "";
    if (!selectedFiles.length) return;
    const uploads = await Promise.all(
      selectedFiles.map(async (file) => {
        const [preview, result] = await Promise.all([
          readImage(file),
          uploadFile(file),
        ]);
        return result?.success && result.uploadId
          ? { preview, uploadId: result.uploadId }
          : null;
      }),
    );
    const uploaded = uploads.filter(Boolean);
    setGalleryImages((current) => [
      ...current,
      ...uploaded.map((image) => image.preview),
    ]);
    setGalleryImageUploadIds((current) => [
      ...current,
      ...uploaded.map((image) => image.uploadId),
    ]);
  };

  const handleProductCatalogue = async (event) => {
    const file = event.target.files?.[0];
    if (!file || isView) return;
    event.target.value = "";
    const result = await uploadFile(file);
    if (!result?.success || !result.uploadId) return;
    setForm((current) => ({
      ...current,
      productCatalogue: result.uploadId,
      productCatalogueFileName: file.name,
    }));
  };

  const handleProductSpecifications = async (event) => {
    const file = event.target.files?.[0];
    if (!file || isView) return;
    event.target.value = "";
    const result = await uploadFile(file);
    if (!result?.success || !result.uploadId) return;
    setForm((current) => ({
      ...current,
      productSpecifications: result.uploadId,
      productSpecificationsFileName: file.name,
    }));
  };
  const removeGalleryImage = (index) => {
    if (isView) {
      return;
    }

    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    setGalleryImageUploadIds((prev) => prev.filter((_, i) => i !== index));
  };

  const selectedGroupsForSubmit = selectionGroups.filter(
    (group) =>
      group.sectorId && (group.productIds?.[0] || group.customProducts?.[0]),
  );
  const quantityForSubmit = Number(form.quantity);
  const priceForSubmit = Number(form.price);
  const hasRequiredProductFields = Boolean(
    selectedGroupsForSubmit.length > 0 &&
      form.quantity !== "" &&
      Number.isFinite(quantityForSubmit) &&
      quantityForSubmit >= 0 &&
      normalizeQuantityUnit(form.quantityUnit) &&
      form.price !== "" &&
      Number.isFinite(priceForSubmit) &&
      priceForSubmit >= 0 &&
      form.currency &&
      mainImage &&
      mainImageUploadId &&
      form.description.trim(),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView || vendorProductState.mutationLoading) return;
    if (fileUploadCount > 0) {
      toogleErrorMessage("Please wait for file uploads to finish.");
      return;
    }

    const selectedGroups = selectionGroups.filter(
      (group) =>
        group.sectorId && (group.productIds?.[0] || group.customProducts?.[0]),
    );
    if (!selectedGroups.length) {
      toogleErrorMessage(t("validation.chooseSectorProduct"));
      return;
    }
    const quantity = Number(form.quantity);
    const price = Number(form.price);
    const quantityUnit = normalizeQuantityUnit(form.quantityUnit);
    if (!Number.isFinite(quantity) || quantity < 0 || form.quantity === "") {
      toogleErrorMessage(t("validation.validMoq"));
      return;
    }
    if (!quantityUnit) {
      toogleErrorMessage(t("validation.selectQuantityUnit"));
      return;
    }
    if (!Number.isFinite(price) || price < 0 || form.price === "") {
      toogleErrorMessage(t("validation.validPrice"));
      return;
    }
    if (!form.currency) {
      toogleErrorMessage(t("ui.selectCurrency"));
      return;
    }

    if (!mainImage || !mainImageUploadId) {
      toogleErrorMessage(t("validation.uploadMainImage"));
      return;
    }
    if (!form.description.trim()) {
      toogleErrorMessage(t("validation.productDescriptionRequired"));
      return;
    }
    const optionalNumbers = [
      form.normalLeadTime,
      form.productionCapacity,
      form.monthlySupplyCapacity,
      form.annualSupplyCapacity,
    ];
    if (
      optionalNumbers.some(
        (value) =>
          value !== "" &&
          (!Number.isFinite(Number(value)) || Number(value) < 0),
      )
    ) {
      toogleErrorMessage(t("validation.validSupplyValues"));
      return;
    }

    const selectedSupplyCapacity =
      form.monthlySupplyCapacity !== ""
        ? form.monthlySupplyCapacity
        : form.annualSupplyCapacity;
    const selectedSupplyPeriod =
      form.monthlySupplyCapacity !== ""
        ? "monthly"
        : form.annualSupplyCapacity !== ""
          ? "annual"
          : "monthly";
    const submitStatus =
      e.nativeEvent?.submitter?.value || form.status || "draft";
    const selection = selectedGroups[0];
    const selectedProductId = selection.productIds?.[0] || "";
    const listedProduct = selectedProductId
      ? (vendorProductState.sectorProducts?.[selection.sectorId] || []).find(
          (item) =>
            String(item?._id || item?.id || item?.productId || "") ===
            String(selectedProductId),
        )
      : null;
    const productName =
      listedProduct?.name ||
      listedProduct?.title ||
      listedProduct?.productName ||
      listedProduct?.templateName ||
      selection.customProducts?.[0] ||
      form.productName;
    const leadTime = formatAmount(
      form.normalLeadTime === "" ? "" : Number(form.normalLeadTime),
      form.normalLeadTimeUnit,
    );
    const productionCapacity =
      form.productionCapacity === ""
        ? ""
        : `${Number(form.productionCapacity)} ${form.productionCapacityUnit} per month`;
    const supplyCapacityText =
      selectedSupplyCapacity === ""
        ? ""
        : `${Number(selectedSupplyCapacity)} ${form.supplyCapacityUnit} ${selectedSupplyPeriod === "annual" ? "annually" : "monthly"}`;
    const productDetails = {
      sectorId: selection.sectorId,
      productName,
      productsSupplied: productName ? [productName] : [],
      servicesSupplied: splitTextValues(form.servicesSupplied),
      productDescriptions: form.description,
      brandsRepresented: splitTextValues(form.brandsRepresented),
      manufacturerDistributor: manufacturerValueForApi(
        form.manufacturerDistributor,
      ),
      countriesManufactured: countryNamesForApi(form.countriesManufactured),
      countriesSupplied: countryNamesForApi(form.countriesSupplied),
      productCatalogue: getUploadReferences(form.productCatalogue),
      productSpecifications: getUploadReferences(form.productSpecifications),
      hsCodes: splitTextValues(form.hsCodes),
      moq: formatAmount(quantity, quantityUnit),
      normalLeadTime: leadTime,
      productionCapacity,
      monthlyAnnualSupplyCapacity: supplyCapacityText,
      quantity,
      price,
      currencySymbol,
      quantityUnit,
      mainImage: mainImageUploadId,
      images: galleryImageUploadIds.filter(Boolean),
      status: submitStatus,
    };
    const payloads = [productDetails];

    if (isEdit) {
      const productId =
        selectedProduct?.apiId || selectedProduct?.productId || id;
      const response = await dispatch(
        updateVendorProductAction(productId, productDetails),
      );
      if (isVendorProductResponseSuccessful(response)) {
        dispatch(getVendorProductsAction({ page: 1, limit: 10 }));
        if (onSave) onSave(payloads[0], mode);
        navigate("/product");
      }
      return;
    }

    try {
      const result = await dispatch(createVendorProductsAction(payloads));
      if (result.createdCount > 0) {
        dispatch(getVendorProductsAction({ page: 1, limit: 10 }));
      }
      if (result.success || result.createdCount > 0) {
        if (onSave) onSave(payloads, mode);
        navigate("/product");
        return;
      }
    } catch (error) {
      toogleErrorMessage(
        error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t("feedback.addProductFailed"),
      );
    }
  };

  /*
   * Finds symbol according to
   * selected currency.
   *
   * USD => $
   * EUR => €
   * GBP => £
   * INR => ₹
   * etc.
   */
  const currencySymbol = useMemo(() => {
    const selectedCurrency = CURRENCY_OPTIONS.find(
      (currency) => currency.code === form.currency,
    );

    return selectedCurrency?.symbol || "$";
  }, [form.currency]);

  const pageTitle = t(
    isView ? "ui.viewProduct" : isEdit ? "ui.editProduct" : "ui.createProduct",
  );
  const pageHeadingVerb = isView
    ? t("ui.viewProduct")
    : isEdit
      ? t("ui.editProduct")
      : t("ui.create");
  const productHeading = t("ui.product");
  const pageHeading = sentenceCaseLabel(
    pageHeadingVerb
      .toLocaleLowerCase()
      .includes(productHeading.toLocaleLowerCase())
      ? pageHeadingVerb
      : `${pageHeadingVerb} ${productHeading}`,
  );

  const pageDescription = t(
    isView
      ? "ui.reviewProductDescription"
      : isEdit
        ? "ui.updateProductDescription"
        : "ui.createProductDescription",
  );
  const catalogueFileLabel =
    form.productCatalogueFileName ||
    (form.productCatalogue ? t("ui.document.uploaded") : "");
  const specificationsFileLabel =
    form.productSpecificationsFileName ||
    (form.productSpecifications
      ? String(form.productSpecifications).startsWith("data:")
        ? t("ui.document.uploaded")
        : String(form.productSpecifications)
      : "");

  if (!isAdd && vendorProductState.detailLoading && !selectedProduct) {
    return (
      <div className="add-product-page">
        <div className="container-fluid add-product-container">
          <LoadingSpinner
            className="page-loading-state"
            label={t("ui.loadingProductDetails")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="add-product-page">
      <div className="container-fluid add-product-container">
        <div className="row align-items-center add-product-header">
          <div className="col-lg-8">
            <div className="product-page-label">
              <Link to="/product" className="product-breadcrumb-link">
                {t("ui.product")}
              </Link>

              <span className="breadcrumb-separator">/</span>

              <span>{pageTitle}</span>
            </div>
          </div>
        </div>

        {!isAdd && !selectedProduct && (
          <div className="alert alert-warning" role="alert">
            {t(
              "ui.product.data.was.not.found.open.this.product.again.from.the.products.page",
            )}
          </div>
        )}

        <div className="product-page-heading">
          <h1>{pageHeading}</h1>
          <p>{pageDescription}</p>
        </div>
        <div className="row g-4 add-product-content-layout">
          <div className="col-md-8">
            <form
              id="addProductForm"
              onSubmit={handleSubmit}
              noValidate
              className="product-editor"
            >
              <section className="editor-section product-information-section">
                <div className="ap-section-heading">
                  <span className="ap-step-number">1</span>
                  <div>
                    <h2>{t("ui.product.basics")}</h2>
                    <p>{t("ui.product.basics.description")}</p>
                  </div>
                  <span className="ap-required-badge">{t("ui.required")}</span>
                </div>

                <div className="product-information-selector">
                  <VendorProductSelector
                    value={selectionGroups}
                    onChange={setSelectionGroups}
                    disabled={isView || isEdit}
                    productsLabel={t("ui.productsSupplied")}
                    searchable
                    required
                  />
                </div>
                <div className="row g-3 ap-basics-grid">
                  <div className="col-md-6">
                    <div className="product-price-field">
                      <ProductField label={t("ui.price")} required>
                        <div
                          className={
                            "product-price-input ap-price-control product-price-input-top" +
                            (isView ? " product-price-input-view" : "")
                          }
                        >
                          <div className="product-currency-select-wrapper">
                            <CustomDropdown
                              className="product-currency-dropdown"
                              ariaLabel={t("ui.selectCurrency")}
                              value={form.currency}
                              options={CURRENCY_DROPDOWN_OPTIONS}
                              placeholder={t("ui.currency")}
                              disabled={isView}
                              searchable
                              searchPlaceholder={t("ui.searchCurrencies")}
                              emptyMessage={t("ui.noCurrencies")}
                              onChange={(currency) =>
                                setForm((current) => ({ ...current, currency }))
                              }
                            />
                          </div>
                          <div className="product-price-value-wrapper">
                            <span className="product-price-currency-symbol">
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              onKeyDown={handleNumericInputKeyDown}
                              name="price"
                              value={form.price}
                              onChange={handleChange}
                              disabled={isView}
                              placeholder={t("ui.0.00")}
                              min="0"
                              step="0.01"
                              required={!isView}
                            />
                          </div>
                        </div>
                      </ProductField>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <ProductField label={t("ui.moq")} required>
                      <div
                        className={
                          "product-quantity-input" +
                          (isView ? " product-quantity-input-view" : "")
                        }
                      >
                        <input
                          type="number"
                          onKeyDown={handleNumericInputKeyDown}
                          name="quantity"
                          value={form.quantity}
                          onChange={handleChange}
                          disabled={isView}
                          placeholder={t("ui.0")}
                          min="0"
                          step="any"
                          required={!isView}
                        />
                        <CustomDropdown
                          className="product-quantity-unit-dropdown"
                          ariaLabel={t("ui.selectQuantityUnit")}
                          value={form.quantityUnit}
                          options={QUANTITY_UNIT_OPTIONS}
                          placeholder={t("ui.unit")}
                          disabled={isView}
                          onChange={(value) =>
                            setForm((current) => ({
                              ...current,
                              quantityUnit: normalizeQuantityUnit(value),
                            }))
                          }
                        />
                      </div>
                    </ProductField>
                  </div>
                </div>
              </section>

              <section className="editor-section ap-media-description-section">
                <div className="ap-section-heading">
                  <span className="ap-step-number">2</span>
                  <div>
                    <h2>{t("ui.media.description")}</h2>
                    <p>{t("ui.media.description.detail")}</p>
                  </div>
                  <span className="ap-required-badge">{t("ui.required")}</span>
                </div>
                <div className="row g-3 product-media-row">
                  <div className="col-md-6">
                    <ProductField label={t("ui.main.image")} required>
                      <div
                        className={
                          "product-media-dropzone product-main-image-upload" +
                          (mainImage ? " has-image" : "") +
                          (isView ? " is-readonly" : "")
                        }
                      >
                        {mainImage ? (
                          <img
                            className="product-media-image"
                            src={mainImage}
                            alt={t("ui.product.meu6z3")}
                          />
                        ) : (
                          <span className="product-media-upload-copy">
                            {!isView && (
                              <span
                                className="ap-media-upload-icon"
                                aria-hidden="true"
                              >
                                <svg viewBox="0 0 24 24" fill="none">
                                  <rect
                                    x="3.5"
                                    y="4.5"
                                    width="17"
                                    height="15"
                                    rx="2"
                                  />
                                  <circle cx="9" cy="9" r="1.5" />
                                  <path d="m5.5 17 4.25-4.25 2.75 2.75 3-3.5L19 16.5" />
                                </svg>
                              </span>
                            )}
                            <span className="ap-media-upload-description">
                              <strong>
                                {isView
                                  ? t("ui.noProductImage")
                                  : t("ui.upload.main.image")}
                              </strong>
                              {!isView && (
                                <small>{t("ui.select.image.file")}</small>
                              )}
                            </span>
                          </span>
                        )}
                        {!isView && (
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleMainImage}
                            aria-label={t("ui.main.image")}
                          />
                        )}
                      </div>
                      {mainImage && !isView && (
                        <button
                          type="button"
                          className="product-media-remove"
                          onClick={() => setMainImage("")}
                        >
                          {t("ui.remove")}
                        </button>
                      )}
                    </ProductField>
                  </div>
                  <div className="col-md-6">
                    <ProductField label={t("ui.additional.images.optional")}>
                      {galleryImages.length < 4 && !isView ? (
                        <div className="product-media-dropzone product-gallery-upload">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            multiple
                            onChange={handleGalleryImages}
                            aria-label={t("ui.additional.images.optional")}
                          />
                          <span className="product-media-upload-copy">
                            <span
                              className="ap-media-upload-icon"
                              aria-hidden="true"
                            >
                              <svg viewBox="0 0 24 24" fill="none">
                                <rect
                                  x="3.5"
                                  y="4.5"
                                  width="17"
                                  height="15"
                                  rx="2"
                                />
                                <circle cx="9" cy="9" r="1.5" />
                                <path d="m5.5 17 4.25-4.25 2.75 2.75 3-3.5L19 16.5" />
                              </svg>
                            </span>
                            <span className="ap-media-upload-description">
                              <strong>{t("ui.add.more.images")}</strong>
                              <small>{t("ui.select.images.files")}</small>
                            </span>
                          </span>
                        </div>
                      ) : (
                        <div className="product-media-dropzone product-gallery-upload is-readonly">
                          <span className="product-media-upload-copy">
                            <strong>
                              {isView
                                ? t("ui.product.gallery")
                                : t("ui.gallery.limit.reached")}
                            </strong>
                            <small>
                              {galleryImages.length}{" "}
                              {t("ui.of.4.images.uploaded")}
                            </small>
                          </span>
                        </div>
                      )}
                      {galleryImages.length > 0 && (
                        <div className="ap-gallery-previews">
                          {galleryImages.slice(0, 4).map((image, index) => (
                            <div
                              className="ap-gallery-thumbnail"
                              key={"gallery-preview-" + index}
                            >
                              <img
                                src={image}
                                alt={t("ui.image") + " " + (index + 1)}
                              />
                              {!isView && (
                                <button
                                  type="button"
                                  onClick={() => removeGalleryImage(index)}
                                  aria-label={
                                    t("ui.remove.image") + " " + (index + 1)
                                  }
                                >
                                  <CloseIcon />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </ProductField>
                  </div>
                </div>

                <div className="product-description-field">
                  <ProductField label={t("ui.productDescriptions")} required>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      disabled={isView}
                      maxLength={500}
                      required={!isView}
                      placeholder={t(
                        "ui.add.product.details.specifications.materials.features.packaging.and.other.useful.information",
                      )}
                    />
                    <div className="ap-description-count">
                      {form.description.length}/500
                    </div>
                  </ProductField>
                </div>
              </section>

              <details className="editor-section ap-optional-section product-supply-details-section">
                <summary className="ap-section-heading ap-supply-heading ap-optional-summary">
                  <span className="ap-step-number">3</span>
                  <div>
                    <h2>{t("ui.supply.details")}</h2>
                    <p>{t("ui.supply.additional.details.description")}</p>
                  </div>
                  <span className="ap-optional-badge">{t("ui.optional")}</span>
                </summary>
                <div className="ap-optional-section-content ap-supply-content">
                  <section className="ap-supply-group">
                    <div className="ap-supply-group-heading">
                      <span className="ap-supply-group-index">01</span>
                      <div>
                        <h3>{t("ui.supply.group.profile")}</h3>
                        <p>{t("ui.supply.group.profile.description")}</p>
                      </div>
                    </div>
                    <div className="row g-3 ap-supply-grid">
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.servicesSupplied")}>
                          <input
                            type="text"
                            name="servicesSupplied"
                            value={form.servicesSupplied}
                            onChange={handleChange}
                            disabled={isView}
                            placeholder={t("ui.servicesSuppliedPlaceholder")}
                          />
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.brandsRepresented")}>
                          <input
                            type="text"
                            name="brandsRepresented"
                            value={form.brandsRepresented}
                            onChange={handleChange}
                            disabled={isView}
                            placeholder={t("ui.brandsRepresentedPlaceholder")}
                          />
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.manufacturerDistributor")}>
                          <CustomDropdown
                            className="supplier-detail-dropdown"
                            ariaLabel={t("ui.manufacturerDistributor")}
                            value={form.manufacturerDistributor}
                            options={[
                              {
                                value: "manufacturer",
                                label: t("ui.manufacturerOption"),
                              },
                              {
                                value: "distributor",
                                label: t("ui.distributorOption"),
                              },
                              {
                                value: "both",
                                label: t("ui.manufacturerDistributorOption"),
                              },
                            ]}
                            placeholder={t("ui.selectManufacturerDistributor")}
                            disabled={isView}
                            onChange={(manufacturerDistributor) =>
                              setForm((current) => ({
                                ...current,
                                manufacturerDistributor,
                              }))
                            }
                          />
                        </ProductField>
                      </div>
                    </div>
                  </section>
                  <section className="ap-supply-group">
                    <div className="ap-supply-group-heading">
                      <span className="ap-supply-group-index">02</span>
                      <div>
                        <h3>{t("ui.supply.group.origin")}</h3>
                        <p>{t("ui.supply.group.origin.description")}</p>
                      </div>
                    </div>
                    <div className="row g-3 ap-supply-grid">
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.countriesManufactured")}>
                          <CustomMultiSelectDropdown
                            value={form.countriesManufactured}
                            options={COUNTRY_OPTIONS}
                            ariaLabel={t("ui.countriesManufactured")}
                            placeholder={t("ui.selectCountries")}
                            searchPlaceholder={t("ui.searchCountries")}
                            emptyMessage={t("ui.noCountriesFound")}
                            disabled={isView}
                            onChange={(countriesManufactured) =>
                              setForm((current) => ({
                                ...current,
                                countriesManufactured,
                              }))
                            }
                          />
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.countriesSupplied")}>
                          <CustomMultiSelectDropdown
                            value={form.countriesSupplied}
                            options={COUNTRY_OPTIONS}
                            ariaLabel={t("ui.countriesSupplied")}
                            placeholder={t("ui.selectCountries")}
                            searchPlaceholder={t("ui.searchCountries")}
                            emptyMessage={t("ui.noCountriesFound")}
                            disabled={isView}
                            onChange={(countriesSupplied) =>
                              setForm((current) => ({
                                ...current,
                                countriesSupplied,
                              }))
                            }
                          />
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.hsCodes")}>
                          <input
                            type="text"
                            name="hsCodes"
                            value={form.hsCodes}
                            onChange={handleChange}
                            disabled={isView}
                            placeholder={t("ui.hsCodesPlaceholder")}
                          />
                        </ProductField>
                      </div>
                    </div>
                  </section>
                  <section className="ap-supply-group ap-supply-group-last">
                    <div className="ap-supply-group-heading">
                      <span className="ap-supply-group-index">03</span>
                      <div>
                        <h3>{t("ui.supply.group.capacity")}</h3>
                        <p>{t("ui.supply.group.capacity.description")}</p>
                      </div>
                    </div>
                    <div className="row g-3 ap-supply-grid">
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.normalLeadTime")}>
                          <div
                            className={
                              "product-quantity-input" +
                              (isView ? " product-quantity-input-view" : "")
                            }
                          >
                            <input
                              type="number"
                              onKeyDown={handleNumericInputKeyDown}
                              name="normalLeadTime"
                              value={form.normalLeadTime}
                              onChange={handleChange}
                              disabled={isView}
                              min="0"
                              step="1"
                              placeholder="0"
                            />
                            <CustomDropdown
                              className="product-quantity-unit-dropdown supplier-detail-unit-dropdown"
                              ariaLabel={t("ui.normalLeadTimeUnit")}
                              value={form.normalLeadTimeUnit}
                              options={LEAD_TIME_UNIT_OPTIONS}
                              disabled={isView}
                              onChange={(normalLeadTimeUnit) =>
                                setForm((current) => ({
                                  ...current,
                                  normalLeadTimeUnit,
                                }))
                              }
                            />
                          </div>
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.productionCapacity")}>
                          <div
                            className={
                              "product-quantity-input" +
                              (isView ? " product-quantity-input-view" : "")
                            }
                          >
                            <input
                              type="number"
                              onKeyDown={handleNumericInputKeyDown}
                              name="productionCapacity"
                              value={form.productionCapacity}
                              onChange={handleChange}
                              disabled={isView}
                              min="0"
                              step="any"
                              placeholder="0"
                            />
                            <CustomDropdown
                              className="product-quantity-unit-dropdown supplier-detail-unit-dropdown"
                              ariaLabel={t("ui.productionCapacityUnit")}
                              value={form.productionCapacityUnit}
                              options={QUANTITY_UNIT_OPTIONS}
                              searchable
                              searchPlaceholder={t("ui.searchUnits")}
                              emptyMessage={t("ui.noUnitsFound")}
                              disabled={isView}
                              onChange={(productionCapacityUnit) =>
                                setForm((current) => ({
                                  ...current,
                                  productionCapacityUnit,
                                }))
                              }
                            />
                          </div>
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.monthly.supply.capacity")}>
                          <div
                            className={
                              "product-quantity-input" +
                              (isView ? " product-quantity-input-view" : "")
                            }
                          >
                            <input
                              type="number"
                              onKeyDown={handleNumericInputKeyDown}
                              name="monthlySupplyCapacity"
                              value={form.monthlySupplyCapacity}
                              onChange={handleChange}
                              disabled={isView}
                              min="0"
                              step="any"
                              placeholder={t("ui.monthly.capacity.placeholder")}
                            />
                            <CustomDropdown
                              className="product-quantity-unit-dropdown supplier-detail-unit-dropdown"
                              ariaLabel={t("ui.supplyCapacityUnit")}
                              value={form.supplyCapacityUnit}
                              options={QUANTITY_UNIT_OPTIONS}
                              searchable
                              searchPlaceholder={t("ui.searchUnits")}
                              emptyMessage={t("ui.noUnitsFound")}
                              disabled={isView}
                              onChange={(supplyCapacityUnit) =>
                                setForm((current) => ({
                                  ...current,
                                  supplyCapacityUnit,
                                }))
                              }
                            />
                          </div>
                        </ProductField>
                      </div>
                      <div className="col-md-6 col-xl-4">
                        <ProductField label={t("ui.annual.supply.capacity")}>
                          <div
                            className={
                              "product-quantity-input" +
                              (isView ? " product-quantity-input-view" : "")
                            }
                          >
                            <input
                              type="number"
                              onKeyDown={handleNumericInputKeyDown}
                              name="annualSupplyCapacity"
                              value={form.annualSupplyCapacity}
                              onChange={handleChange}
                              disabled={isView}
                              min="0"
                              step="any"
                              placeholder={t("ui.annual.capacity.placeholder")}
                            />
                            <CustomDropdown
                              className="product-quantity-unit-dropdown supplier-detail-unit-dropdown"
                              ariaLabel={t("ui.supplyCapacityUnit")}
                              value={form.supplyCapacityUnit}
                              options={QUANTITY_UNIT_OPTIONS}
                              searchable
                              searchPlaceholder={t("ui.searchUnits")}
                              emptyMessage={t("ui.noUnitsFound")}
                              disabled={isView}
                              onChange={(supplyCapacityUnit) =>
                                setForm((current) => ({
                                  ...current,
                                  supplyCapacityUnit,
                                }))
                              }
                            />
                          </div>
                        </ProductField>
                      </div>
                    </div>
                  </section>
                </div>
              </details>

              <details className="editor-section ap-optional-section ap-documents-section">
                <summary className="ap-section-heading ap-optional-summary">
                  <span className="ap-step-number">4</span>
                  <div>
                    <h2>{t("ui.documents.certifications")}</h2>
                    <p>{t("ui.documents.certifications.description")}</p>
                  </div>
                  <span className="ap-optional-badge">{t("ui.optional")}</span>
                </summary>
                <div className="ap-optional-section-content ap-documents-content">
                  <div className="row g-3 ap-documents-grid">
                    <div className="col-md-6">
                      <ProductField label={t("ui.productCatalogueOptional")}>
                        <div
                          className={
                            "ap-document-upload" +
                            (catalogueFileLabel ? " has-file" : "") +
                            (isView ? " is-readonly" : "")
                          }
                        >
                          <span className="ap-document-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                              <path d="M6 3.75h8l4 4v12.5H6a2 2 0 0 1-2-2v-12.5a2 2 0 0 1 2-2Z" />
                              <path d="M14 4v4h4M8 13h8M8 16.5h6" />
                            </svg>
                          </span>
                          <span className="ap-document-copy">
                            <strong>
                              {catalogueFileLabel ||
                                (isView
                                  ? t("ui.noProductCatalogueUploaded")
                                  : t("ui.choose.file"))}
                            </strong>
                            <small>
                              {catalogueFileLabel
                                ? isView
                                  ? t("ui.document.attached")
                                  : t("ui.document.ready")
                                : t("ui.document.fileTypes")}
                            </small>
                          </span>
                          {!isView && (
                            <span className="ap-document-action">
                              {catalogueFileLabel
                                ? t("ui.replace.file")
                                : t("ui.upload.file")}
                            </span>
                          )}
                          {!isView && (
                            <input
                              type="file"
                              accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
                              onChange={handleProductCatalogue}
                              aria-label={t("ui.productCatalogue")}
                            />
                          )}
                        </div>
                      </ProductField>
                    </div>
                    <div className="col-md-6">
                      <ProductField
                        label={t("ui.productSpecificationsOptional")}
                      >
                        <div
                          className={
                            "ap-document-upload" +
                            (specificationsFileLabel ? " has-file" : "") +
                            (isView ? " is-readonly" : "")
                          }
                        >
                          <span className="ap-document-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                              <path d="M6 3.75h8l4 4v12.5H6a2 2 0 0 1-2-2v-12.5a2 2 0 0 1 2-2Z" />
                              <path d="M14 4v4h4M8 13h8M8 16.5h6" />
                            </svg>
                          </span>
                          <span className="ap-document-copy">
                            <strong>
                              {specificationsFileLabel ||
                                (isView
                                  ? t("ui.no.specifications.uploaded")
                                  : t("ui.choose.file"))}
                            </strong>
                            <small>
                              {specificationsFileLabel
                                ? isView
                                  ? t("ui.document.attached")
                                  : t("ui.document.ready")
                                : t("ui.document.fileTypes")}
                            </small>
                          </span>
                          {!isView && (
                            <span className="ap-document-action">
                              {specificationsFileLabel
                                ? t("ui.replace.file")
                                : t("ui.upload.file")}
                            </span>
                          )}
                          {!isView && (
                            <input
                              type="file"
                              accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
                              onChange={handleProductSpecifications}
                              aria-label={t("ui.productSpecifications")}
                            />
                          )}
                        </div>
                      </ProductField>
                    </div>
                  </div>
                </div>
              </details>
              {/* FOOTER */}
              <div
                className={
                  "product-editor-footer ap-product-footer" +
                  (isView ? " ap-product-footer-view" : "")
                }
              >
                <div className="ap-footer-actions">
                  {!isAdd && (
                    <Link
                      to="/product"
                      className={
                        "ap-button ap-button-secondary" +
                        (isView ? " ap-view-back-button" : "")
                      }
                    >
                      {isView ? t("ui.back") : t("ui.cancel")}
                    </Link>
                  )}
                  {isView && selectedProduct && (
                    <button
                      type="button"
                      className="ap-button ap-button-publish ap-view-edit-button"
                      onClick={() =>
                        navigate(`/product/edit/${selectedProduct.id || id}`, {
                          state: { product: selectedProduct },
                        })
                      }
                    >
                      {t("ui.edit")}
                    </button>
                  )}
                  {!isView && isAdd && (
                    <>
                      <button
                        type="button"
                        className="ap-button ap-button-secondary"
                        onClick={() => navigate("/product")}
                      >
                        {t("ui.cancel")}
                      </button>
                      <button
                        type="submit"
                        name="submitStatus"
                        value="active"
                        className="ap-button ap-button-publish"
                        disabled={
                          !hasRequiredProductFields ||
                          vendorProductState.mutationLoading
                        }
                      >
                        {vendorProductState.mutationLoading
                          ? t("ui.saving")
                          : t("ui.submit")}
                      </button>
                    </>
                  )}
                  {!isView && isEdit && (
                    <button
                      type="submit"
                      name="submitStatus"
                      value={form.status || "draft"}
                      className="ap-button ap-button-publish"
                      disabled={
                        !hasRequiredProductFields ||
                        vendorProductState.mutationLoading
                      }
                    >
                      {vendorProductState.mutationLoading
                        ? t("ui.saving")
                        : t("ui.update")}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="col-md-4">
            <div className="product-sidebar">
              <section className="sidebar-card preview-sidebar-card ap-preview-card">
                <div className="ap-sidebar-heading">
                  <span className="ap-sidebar-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M2.5 12s3.3-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.3 6.5-9.5 6.5S2.5 12 2.5 12Z" />
                      <circle cx="12" cy="12" r="3.2" />
                    </svg>
                  </span>
                  <div>
                    <h3>{t("ui.product.preview")}</h3>
                    <p>{t("ui.product.preview.description")}</p>
                  </div>
                </div>
                <div className="live-product-card ap-live-product-card">
                  <div className="live-product-image">
                    {mainImage ? (
                      <img src={mainImage} alt={t("ui.product.meu6z3")} />
                    ) : (
                      <div className="live-image-placeholder">
                        <strong>{t("ui.product.image.preview")}</strong>
                        <span>{t("ui.add.image.to.see.preview")}</span>
                      </div>
                    )}
                  </div>
                  <div className="live-product-content">
                    <span className="live-category">
                      {form.sectorName || t("ui.sector")}
                    </span>
                    <h4>{form.productName || t("ui.productName")}</h4>
                    <p>
                      {form.description
                        ? form.description.length > 90
                          ? `${form.description.substring(0, 90)}...`
                          : form.description
                        : t("ui.productDescriptionPreview")}
                    </p>
                    <div className="live-price-row">
                      <small>{t("ui.price")}</small>
                      <strong>
                        {currencySymbol}
                        {form.price || "0.00"}
                      </strong>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductField({ label, required, children }) {
  const { text } = useTranslation();
  return (
    <div className="product-field">
      <label>
        {text(label)}

        {required && <span>*</span>}
      </label>

      {children}
    </div>
  );
}

function StatusCard({ name, value, title, text, selected, onChange }) {
  return (
    <label
      className={
        selected === value
          ? `product-status-card ${value} selected`
          : `product-status-card ${value}`
      }
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected === value}
        onChange={onChange}
      />

      <span className="status-circle" />

      <strong>{title}</strong>

      <small>{text}</small>
    </label>
  );
}

function CustomMultiSelectDropdown({
  value = [],
  options,
  onChange,
  ariaLabel,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
}) {
  const { text } = useTranslation();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedOptions = selectedValues.map((selectedValue) =>
    options.find((option) => String(option.value) === String(selectedValue)),
  );
  const selectedLabels = selectedValues.map(
    (selectedValue, index) =>
      selectedOptions[index]?.label || String(selectedValue),
  );
  const query = search.trim().toLowerCase();
  const filteredOptions = query
    ? options.filter((option) =>
        String(option.searchText || option.label || "")
          .toLowerCase()
          .includes(query),
      )
    : options;
  const selectedSummary = selectedLabels.length
    ? selectedLabels.length > 2
      ? selectedLabels.slice(0, 2).join(", ") +
        " +" +
        (selectedLabels.length - 2)
      : selectedLabels.join(", ")
    : text(placeholder || "");

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const toggleOption = (optionValue) => {
    const nextValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((selectedValue) => selectedValue !== optionValue)
      : [...selectedValues, optionValue];
    onChange(nextValues);
  };

  return (
    <div
      ref={rootRef}
      className="product-custom-dropdown product-multi-select-dropdown"
    >
      <button
        ref={triggerRef}
        type="button"
        className="product-custom-dropdown-trigger"
        aria-label={text(ariaLabel)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span
          className={
            "product-custom-dropdown-value" +
            (selectedValues.length ? " has-value" : " is-placeholder")
          }
        >
          {selectedSummary}
        </span>
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
        </svg>
      </button>
      {open && (
        <div className="product-custom-dropdown-menu product-multi-select-menu">
          <div className="product-custom-dropdown-search-wrap">
            <input
              type="search"
              className="product-custom-dropdown-search"
              value={search}
              placeholder={text(searchPlaceholder || "")}
              aria-label={text(searchPlaceholder || ariaLabel)}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div
            className="product-custom-dropdown-options"
            role="listbox"
            aria-label={text(ariaLabel)}
            aria-multiselectable="true"
          >
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const selected = selectedValues.includes(option.value);
                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={
                      "product-custom-dropdown-option product-multi-select-option" +
                      (selected ? " is-selected" : "")
                    }
                    key={option.value}
                    onClick={() => toggleOption(option.value)}
                  >
                    <span>{option.label}</span>
                    <span
                      className="product-multi-select-check"
                      aria-hidden="true"
                    >
                      {selected ? "\u2713" : ""}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="product-custom-dropdown-empty">
                {text(emptyMessage || "")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
